import React, { useEffect, useState, useCallback } from "react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";
import {
  JsonRpcProvider,
  Contract,
  formatUnits,
} from "ethers";

// ==================================================
// BSC RPC
// ==================================================

const RPC_URL = "https://bsc-dataseed.binance.org/";

const provider = new JsonRpcProvider(RPC_URL);

// BSC Mainnet
const BSC_CHAIN_ID = "0x38";

// ==================================================
// DASHBOARD
// ==================================================

const Dashboard = () => {
  // ==================================================
  // WALLET STATE
  // ==================================================

  const [address, setAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // ==================================================
  // CONTRACT
  // ==================================================

  const {
    getUserDashboard,
    getClaimable,
    getGlobalStats,
    getBatchCount,
    getBatch,
    getUserReferralStats,
    withdraw,
    getRewardPoolRemainingUsd,
    getBurnStats,
  } = useContract();

  // ==================================================
  // USER DATA
  // ==================================================

  const [data, setData] = useState({
    totalPaidCards: 0,
    activeCards: 0,
    expiredCards: 0,
    bonusCards: 0,
    totalDeposits: "0",
    rank: 0,
    currentRoiBps: 0,
    nextRank: 0,
    refsRemaining: 0,
    energyWallet: "0",
    referralWallet: "0",
    totalWithdrawnUsd: "0",
    nextWithdrawTime: 0,
    qualifiedRefs: 0,
  });

  const [claimable, setClaimable] = useState({
    energy: "0",
    referral: "0",
  });

  const [pendingBreakdown, setPendingBreakdown] = useState({
    pendingEnergy: "0",
    totalEnergyClaimable: "0",
  });

  // ==================================================
  // GLOBAL STATS
  // ==================================================

  const [globalStats, setGlobalStats] = useState({
    users: 0,
    totalDeposits: "0",
    cardsSold: 0,
    rewardUsdPaid: "0",
    poolBalance: "0",
  });

  // ==================================================
  // BURN STATS
  // ==================================================

  const [burnStats, setBurnStats] = useState({
    totalBurnedUsd: "0",
    totalBurnedKind: "0",
    pendingBurnUsd: "0",
  });

  // ==================================================
  // BATCHES
  // ==================================================

  const [batches, setBatches] = useState([]);

  // ==================================================
  // KIND PRICE
  // ==================================================

  const [kindPrice, setKindPrice] = useState(0);

  // ==================================================
  // LOADING
  // ==================================================

  const [loading, setLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  // ==================================================
  // REFERRAL STATS
  // ==================================================

  const [refStats, setRefStats] = useState({
    totalReferrals: 0,
    teamVolume: 0,
    directVolume: 0,
  });

  // ==================================================
  // PANCAKESWAP
  // ==================================================

  const KIND_BNB_PAIR =
    "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  const KIND_ADDRESS =
    "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";

  const WBNB =
    "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";

  // ==================================================
  // GET INJECTED WALLET
  // ==================================================

  const getWalletProvider = () => {
    if (typeof window === "undefined") {
      return null;
    }

    // Bitget Wallet
    if (window.bitkeep?.ethereum) {
      return window.bitkeep.ethereum;
    }

    // MetaMask / other injected wallet
    if (window.ethereum) {
      return window.ethereum;
    }

    return null;
  };

  // ==================================================
  // CHECK WALLET CONNECTION
  // ==================================================

  const checkWalletConnection = useCallback(async () => {
    try {
      const walletProvider = getWalletProvider();

      if (!walletProvider) {
        setAddress(null);
        setIsConnected(false);
        return;
      }

      const accounts = await walletProvider.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    } catch (error) {
      console.error(
        "Wallet connection check failed:",
        error
      );

      setAddress(null);
      setIsConnected(false);
    }
  }, []);

  // ==================================================
  // SWITCH TO BSC
  // ==================================================

  const switchToBSC = async (walletProvider) => {
    try {
      const chainId = await walletProvider.request({
        method: "eth_chainId",
      });

      if (
        chainId &&
        chainId.toLowerCase() === BSC_CHAIN_ID
      ) {
        return true;
      }

      try {
        await walletProvider.request({
          method: "wallet_switchEthereumChain",
          params: [
            {
              chainId: BSC_CHAIN_ID,
            },
          ],
        });

        return true;
      } catch (switchError) {
        console.error(
          "BSC switch failed:",
          switchError
        );

        // BSC not added
        if (switchError?.code === 4902) {
          try {
            await walletProvider.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: BSC_CHAIN_ID,
                  chainName: "BNB Smart Chain",
                  nativeCurrency: {
                    name: "BNB",
                    symbol: "BNB",
                    decimals: 18,
                  },
                  rpcUrls: [
                    "https://bsc-dataseed.binance.org/",
                  ],
                  blockExplorerUrls: [
                    "https://bscscan.com/",
                  ],
                },
              ],
            });

            return true;
          } catch (addError) {
            console.error(
              "Failed to add BSC:",
              addError
            );

            return false;
          }
        }

        return false;
      }
    } catch (error) {
      console.error(
        "Network check failed:",
        error
      );

      return false;
    }
  };

  // ==================================================
  // CONNECT WALLET
  // ==================================================

  const connectWallet = async () => {
    if (connecting) {
      return false;
    }

    try {
      setConnecting(true);

      const walletProvider = getWalletProvider();

      if (!walletProvider) {
        toast.error(
          "No wallet detected. Please open this website in Bitget Wallet."
        );

        return false;
      }

      // Request account
      const accounts = await walletProvider.request({
        method: "eth_requestAccounts",
      });

      if (!accounts || accounts.length === 0) {
        return false;
      }

      // Set account immediately
      setAddress(accounts[0]);
      setIsConnected(true);

      // Switch to BSC
      const bscReady =
        await switchToBSC(walletProvider);

      if (!bscReady) {
        toast.error(
          "Please switch your wallet to BSC Mainnet."
        );

        return false;
      }

      // Get final account
      const finalAccounts =
        await walletProvider.request({
          method: "eth_accounts",
        });

      if (
        finalAccounts &&
        finalAccounts.length > 0
      ) {
        setAddress(finalAccounts[0]);
        setIsConnected(true);
      }

      toast.success("Wallet connected!");

      return true;
    } catch (error) {
      console.error(
        "Wallet connection failed:",
        error
      );

      if (error?.code === 4001) {
        toast.error(
          "Wallet connection was rejected."
        );
      } else {
        toast.error(
          error?.message ||
            "Failed to connect wallet"
        );
      }

      setAddress(null);
      setIsConnected(false);

      return false;
    } finally {
      setConnecting(false);
    }
  };

  // ==================================================
  // DISCONNECT
  // ==================================================

  const disconnectWallet = () => {
    setAddress(null);
    setIsConnected(false);

    setData({
      totalPaidCards: 0,
      activeCards: 0,
      expiredCards: 0,
      bonusCards: 0,
      totalDeposits: "0",
      rank: 0,
      currentRoiBps: 0,
      nextRank: 0,
      refsRemaining: 0,
      energyWallet: "0",
      referralWallet: "0",
      totalWithdrawnUsd: "0",
      nextWithdrawTime: 0,
      qualifiedRefs: 0,
    });

    setClaimable({
      energy: "0",
      referral: "0",
    });

    setBatches([]);

    console.log(
      "Wallet disconnected from DApp"
    );
  };

  // ==================================================
  // WALLET INITIALIZATION
  // ==================================================

  useEffect(() => {
    checkWalletConnection();
  }, [checkWalletConnection]);

  // ==================================================
  // WALLET EVENTS
  // ==================================================

  useEffect(() => {
    const walletProvider =
      getWalletProvider();

    if (!walletProvider) {
      return;
    }

    // ------------------------------
    // ACCOUNT CHANGED
    // ------------------------------

    const handleAccountsChanged = (
      accounts
    ) => {
      console.log(
        "Accounts changed:",
        accounts
      );

      if (
        accounts &&
        accounts.length > 0
      ) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    };

    // ------------------------------
    // NETWORK CHANGED
    // ------------------------------

    const handleChainChanged = (
      chainId
    ) => {
      console.log(
        "Chain changed:",
        chainId
      );

      checkWalletConnection();
    };

    walletProvider.on(
      "accountsChanged",
      handleAccountsChanged
    );

    walletProvider.on(
      "chainChanged",
      handleChainChanged
    );

    return () => {
      if (
        walletProvider.removeListener
      ) {
        walletProvider.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        walletProvider.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, [checkWalletConnection]);

  // ==================================================
  // FETCH KIND PRICE
  // ==================================================

  const fetchKindPrice =
    useCallback(async () => {
      try {
        const pair = new Contract(
          KIND_BNB_PAIR,
          [
            "function getReserves() view returns(uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
            "function token0() view returns(address)",
            "function token1() view returns(address)",
          ],
          provider
        );

        const [
          reserves,
          token0,
          token1,
        ] = await Promise.all([
          pair.getReserves(),
          pair.token0(),
          pair.token1(),
        ]);

        let kindReserve;
        let bnbReserve;

        if (
          token0.toLowerCase() ===
          KIND_ADDRESS.toLowerCase()
        ) {
          kindReserve = reserves[0];
          bnbReserve = reserves[1];
        } else {
          kindReserve = reserves[1];
          bnbReserve = reserves[0];
        }

        const kindAmount =
          parseFloat(
            formatUnits(
              kindReserve,
              18
            )
          );

        const bnbAmount =
          parseFloat(
            formatUnits(
              bnbReserve,
              18
            )
          );

        if (
          kindAmount > 0 &&
          bnbAmount > 0
        ) {
          const kindPriceInBNB =
            bnbAmount /
            kindAmount;

          // Binance BNB price
          const bnbUsdResponse =
            await fetch(
              "https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"
            );

          const bnbUsdData =
            await bnbUsdResponse.json();

          const bnbUsdPrice =
            parseFloat(
              bnbUsdData.price
            );

          const kindUsdPrice =
            kindPriceInBNB *
            bnbUsdPrice;

          setKindPrice(
            kindUsdPrice
          );
        }
      } catch (err) {
        console.error(
          "Price fetch error:",
          err
        );
      }
    }, []);

  // ==================================================
  // USD TO KIND
  // ==================================================

  const usdToKind = (
    usdAmount
  ) => {
    if (
      kindPrice <= 0 ||
      !usdAmount
    ) {
      return "0";
    }

    return (
      parseFloat(
        usdAmount
      ) /
      kindPrice
    ).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }
    );
  };

  // ==================================================
  // REFRESH USER STATS
  // ==================================================

  const refreshUserStats =
    useCallback(async () => {
      if (
        !isConnected ||
        !address
      ) {
        return;
      }

      setLoading(true);

      try {
        const [
          dashboard,
          claimableData,
        ] = await Promise.all([
          getUserDashboard(address),
          getClaimable(address),
        ]);

        if (!dashboard) {
          console.error(
            "Dashboard data is null"
          );

          setLoading(false);
          return;
        }

        const settledEnergy =
          parseFloat(
            dashboard?.energyWallet ||
              "0"
          );

        const settledReferral =
          parseFloat(
            dashboard?.referralWallet ||
              "0"
          );

        const totalEnergyClaimable =
          parseFloat(
            claimableData?.energy ||
              "0"
          );

        const totalReferralClaimable =
          parseFloat(
            claimableData?.referral ||
              "0"
          );

        const pendingEnergy =
          Math.max(
            0,
            totalEnergyClaimable -
              settledEnergy
          ).toFixed(4);

        setData({
          totalPaidCards: Number(
            dashboard?.totalPaidCards ||
              0
          ),

          activeCards: Number(
            dashboard?.activeCards ||
              0
          ),

          expiredCards: Number(
            dashboard?.expiredCards ||
              0
          ),

          bonusCards: Number(
            dashboard?.bonusCards ||
              0
          ),

          totalDeposits:
            parseFloat(
              dashboard?.totalDeposits ||
                "0"
            ).toFixed(2),

          rank: Number(
            dashboard?.rank ||
              0
          ),

          currentRoiBps: Number(
            dashboard?.currentRoiBps ||
              65
          ),

          nextRank: Number(
            dashboard?.nextRank ||
              0
          ),

          refsRemaining: Number(
            dashboard?.refsRemaining ||
              0
          ),

          energyWallet:
            settledEnergy.toFixed(
              4
            ),

          referralWallet:
            settledReferral.toFixed(
              4
            ),

          totalWithdrawnUsd:
            parseFloat(
              dashboard?.totalWithdrawnUsd ||
                "0"
            ).toFixed(2),

          nextWithdrawTime:
            Number(
              dashboard?.nextWithdrawTime ||
                0
            ),

          qualifiedRefs:
            Number(
              dashboard?.qualifiedRefs ||
                0
            ),
        });

        setClaimable({
          energy:
            totalEnergyClaimable.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              }
            ),

          referral:
            totalReferralClaimable.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              }
            ),
        });

        setPendingBreakdown({
          pendingEnergy:
            pendingEnergy ===
            "0.0000"
              ? "0"
              : pendingEnergy,

          totalEnergyClaimable:
            totalEnergyClaimable.toFixed(
              4
            ),
        });
      } catch (err) {
        console.error(
          "❌ User stats error:",
          err
        );

        toast.error(
          "Failed to load user data"
        );
      } finally {
        setLoading(false);
      }
    }, [
      isConnected,
      address,
      getUserDashboard,
      getClaimable,
    ]);

  // ==================================================
  // REFRESH GLOBAL STATS
  // ==================================================

  const refreshGlobalStats =
    useCallback(async () => {
      setGlobalLoading(true);

      try {
        const [
          stats,
          poolRemaining,
          burn,
        ] = await Promise.all([
          getGlobalStats(),
          getRewardPoolRemainingUsd(),
          getBurnStats(),
        ]);

        setGlobalStats({
          users: Number(
            stats?.users || 0
          ),

          totalDeposits:
            parseFloat(
              stats?.totalDeposits ||
                "0"
            ).toFixed(2),

          cardsSold: Number(
            stats?.cardsSold ||
              0
          ),

          rewardUsdPaid:
            parseFloat(
              stats?.rewardUsdPaid ||
                "0"
            ).toFixed(2),

          poolBalance:
            parseFloat(
              poolRemaining ||
                "0"
            ).toFixed(2),
        });

        setBurnStats({
          totalBurnedUsd:
            parseFloat(
              burn?.totalBurnedUsd ||
                "0"
            ).toFixed(2),

          totalBurnedKind:
            parseFloat(
              burn?.totalBurnedKind ||
                "0"
            ).toFixed(4),

          pendingBurnUsd:
            parseFloat(
              burn?.pendingBurnUsd ||
                "0"
            ).toFixed(2),
        });
      } catch (err) {
        console.error(
          "❌ Global stats error:",
          err
        );
      } finally {
        setGlobalLoading(false);
      }
    }, [
      getGlobalStats,
      getRewardPoolRemainingUsd,
      getBurnStats,
    ]);

  // ==================================================
  // REFRESH BATCHES
  // ==================================================

  const refreshBatches =
    useCallback(async () => {
      if (!address) {
        setBatches([]);
        return;
      }

      try {
        const count =
          await getBatchCount(
            address
          );

        if (
          Number(count) === 0
        ) {
          setBatches([]);
          return;
        }

        const batchPromises = [];

        for (
          let i = 0;
          i < Number(count);
          i++
        ) {
          batchPromises.push(
            getBatch(
              address,
              i
            )
          );
        }

        const rawBatches =
          await Promise.all(
            batchPromises
          );

        const now =
          Math.floor(
            Date.now() / 1000
          );

        const roiRatesBps = [
          65,
          75,
          90,
          110,
          150,
        ];

        const cardPrice = 50;

        const formatted =
          rawBatches.map(
            (batch, i) => {
              const paidCards =
                Number(
                  batch.paidCards ||
                    0
                );

              const bonusCards =
                Number(
                  batch.bonusCards ||
                    0
                );

              const totalCards =
                paidCards +
                bonusCards;

              const rank =
                Number(
                  batch.userLevelAtStart
                );

              const startTime =
                Number(
                  batch.startTime
                );

              const roi =
                roiRatesBps[
                  rank
                ] || 65;

              const dailyUsd =
                (totalCards *
                  cardPrice *
                  roi) /
                10000;

              const totalMinutes =
                Math.floor(
                  (now -
                    startTime) /
                    60
                );

              const days =
                Math.floor(
                  totalMinutes /
                    1440
                );

              const hours =
                Math.floor(
                  (totalMinutes %
                    1440) /
                    60
                );

              const minutes =
                totalMinutes %
                60;

              let runtime;

              if (days > 0) {
                runtime =
                  `${days}d ${hours}h`;
              } else if (
                hours > 0
              ) {
                runtime =
                  `${hours}h ${minutes}m`;
              } else {
                runtime =
                  `${minutes}m`;
              }

              return {
                id: i + 1,
                totalCards,
                paidCards,
                bonusCards,
                runtime,
                daily:
                  `$${dailyUsd.toFixed(
                    2
                  )}`,
                active:
                  batch.active,
              };
            }
          );

        setBatches(
          formatted
        );
      } catch (err) {
        console.error(
          "Batch error:",
          err
        );

        setBatches([]);
      }
    }, [
      address,
      getBatchCount,
      getBatch,
    ]);

  // ==================================================
  // FETCH PRICE
  // ==================================================

  useEffect(() => {
    fetchKindPrice();

    const interval =
      setInterval(
        fetchKindPrice,
        30000
      );

    return () =>
      clearInterval(
        interval
      );
  }, [
    fetchKindPrice,
  ]);

  // ==================================================
  // LOAD USER DATA
  // ==================================================

  useEffect(() => {
    if (
      isConnected &&
      address
    ) {
      refreshUserStats();
      refreshGlobalStats();
    }
  }, [
    isConnected,
    address,
    refreshUserStats,
    refreshGlobalStats,
  ]);

  // ==================================================
  // LOAD BATCHES
  // ==================================================

  useEffect(() => {
    if (
      !loading &&
      data.totalPaidCards > 0
    ) {
      refreshBatches();
    }
  }, [
    loading,
    data.totalPaidCards,
    refreshBatches,
  ]);

  // ==================================================
  // DAILY ROI
  // ==================================================

  const getDailyRoi = () =>
    (
      data.currentRoiBps /
      100
    ).toFixed(2);

  // ==================================================
  // RANK NAME
  // ==================================================

  const getRankName = () => {
    const ranks = [
      "Starter",
      "Builder",
      "Leader",
      "Pro Leader",
      "Elite",
    ];

    return (
      ranks[data.rank] ||
      "Starter"
    );
  };

  // ==================================================
  // REFERRAL LINK
  // ==================================================

  const referralLink = address
    ? `${window.location.origin}/cards?ref=${address}`
    : "";

  // ==================================================
  // COPY
  // ==================================================

  const copy = (text) => {
    navigator.clipboard.writeText(
      text
    );

    toast.success(
      "✅ Link copied!"
    );
  };

  // ==================================================
  // LOAD REFERRAL STATS
  // ==================================================

  const loadReferralStats =
    useCallback(async () => {
      if (!address) {
        return;
      }

      try {
        const stats =
          await getUserReferralStats(
            address
          );

        setRefStats(stats);
      } catch (err) {
        console.error(
          "Referral stats error:",
          err
        );
      }
    }, [
      address,
      getUserReferralStats,
    ]);

  useEffect(() => {
    loadReferralStats();
  }, [
    loadReferralStats,
  ]);

  // ==================================================
  // READABLE ERROR
  // ==================================================

  const getReadableError = (
    err
  ) => {
    console.error(
      "Raw Error:",
      err
    );

    let message =
      err?.reason ||
      err?.shortMessage ||
      err?.info?.error?.message ||
      err?.info?.error?.data
        ?.message ||
      err?.error?.reason ||
      err?.error?.message ||
      err?.data?.message ||
      err?.message ||
      "Transaction failed";

    const match =
      message.match(
        /execution reverted(?::)?\s*(.*)/i
      );

    if (
      match &&
      match[1]
    ) {
      message =
        match[1];
    }

    message =
      message
        .replace(
          "execution reverted: ",
          ""
        )
        .replace(
          "execution reverted",
          ""
        )
        .replace(
          "VM Exception while processing transaction: reverted with reason string ",
          ""
        )
        .replace(
          /^"/,
          ""
        )
        .replace(
          /"$/,
          ""
        )
        .trim();

    return message;
  };

  // ==================================================
  // WITHDRAW
  // ==================================================

const handleWithdraw = async () => {
  if (!address) {
    toast.error("Wallet not connected");
    return;
  }

  try {
    setClaimLoading(true);

    toast.loading(
      "Please confirm the withdrawal in your wallet...",
      {
        id: "withdraw",
      }
    );

    const txHash = await withdraw();

    console.log("Withdrawal TX:", txHash);

    toast.success("✅ Withdrawal successful!", {
      id: "withdraw",
    });
  } catch (err) {
    console.error("Withdraw Error:", err);

    toast.error(getReadableError(err), {
      id: "withdraw",
      duration: 8000,
    });
  } finally {
    setClaimLoading(false);
  }
};

  // ==================================================
  // NOT CONNECTED SCREEN
  // ==================================================

  if (!isConnected) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-[#111111] px-4">

        <div className="w-full max-w-md bg-[#171717] border border-[#2A2A2A] backdrop-blur-xl rounded-3xl p-8 text-center shadow-2xl">

          <div className="w-20 h-20 mx-auto mb-6 bg-[#D3AF37]/10 border-4 border-[#D3AF37]/30 rounded-2xl flex items-center justify-center">

            <div className="w-12 h-12 border-2 border-[#D3AF37] border-t-transparent rounded-full animate-spin" />

          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-[#D3AF37] mb-4">
            Connect Wallet
          </h2>

          <p className="text-gray-300 mb-8 text-lg">
            Connect to view your dashboard
          </p>

          <button
            onClick={
              connectWallet
            }
            disabled={
              connecting
            }
            className="w-full bg-[#D3AF37] hover:bg-[#B59410] py-4 px-8 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {connecting
              ? "Connecting..."
              : "🔗 Connect Wallet"}
          </button>

        </div>
      </div>
    );
  }

  // ==================================================
  // DASHBOARD
  // ==================================================

  return (
    <div className="min-h-screen p-4 md:p-6 text-white">

      {/* ==================================================
          HEADER
      ================================================== */}

      <section className="mb-12">

        <div className="bg-[#171717] border border-[#2A2A2A] p-6 md:p-8 rounded-3xl shadow-2xl">

          <h1 className="text-3xl md:text-5xl font-black text-[#D3AF37] mb-6">
            Energy Cards Dashboard
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">

            <div className="flex items-center gap-3 text-green-400 text-sm bg-green-900/50 p-3 rounded-xl">

              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />

              <span>
                {address.slice(
                  0,
                  6
                )}
                ...
                {address.slice(
                  -4
                )}
              </span>

            </div>

            <div className="text-gray-400 text-sm font-mono bg-gray-900/50 px-3 py-1 rounded-lg">

              KIND: $
              {kindPrice > 0
                ? kindPrice.toFixed(
                    6
                  )
                : "Loading..."}

            </div>

            <button
              onClick={
                disconnectWallet
              }
              className="px-4 py-2 rounded-xl bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white transition"
            >
              Disconnect
            </button>

          </div>

        </div>

      </section>

      {/* ==================================================
          YOUR STATS
      ================================================== */}

      <section className="mb-12">

        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#D3AF37] flex items-center gap-3">
          📊 Your Stats
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

          <Card
            title="Active Cards"
            value={data.activeCards.toLocaleString()}
            icon="🎴"
          />

          <Card
            title="Total Paid"
            value={data.totalPaidCards.toLocaleString()}
            icon="💳"
          />

          <Card
            title="Bonus Cards"
            value={data.bonusCards.toLocaleString()}
            icon="⭐"
          />

          <Card
            title="Daily ROI"
            value={`${getDailyRoi()}%`}
            color="yellow"
            icon="📈"
          />

          <Card
            title="💰 Energy Wallet (Settled)"
            value={
              <>
                ${data.energyWallet}

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    data.energyWallet
                  )}{" "}
                  KIND)
                </span>
              </>
            }
            subtitle={
              <>
                +$
                {
                  pendingBreakdown.pendingEnergy
                }

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    pendingBreakdown.pendingEnergy
                  )}{" "}
                  KIND)
                </span>
              </>
            }
            color="green"
            icon="⚡"
          />

          <Card
            title="👥 Referral Wallet (Settled)"
            value={
              <>
                ${data.referralWallet}

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    data.referralWallet
                  )}{" "}
                  KIND)
                </span>
              </>
            }
            color="purple"
            icon="💸"
          />

          <Card
            title="Total Withdrawn"
            value={
              <>
                ${data.totalWithdrawnUsd}

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    data.totalWithdrawnUsd
                  )}{" "}
                  KIND)
                </span>
              </>
            }
            color="blue"
            icon="🏦"
          />

          <Card
            title="Rank"
            value={`${getRankName()} ${data.rank}`}
            color="indigo"
            icon="🏆"
          />

        </div>

      </section>

      {/* ==================================================
          TOTAL CLAIMABLE
      ================================================== */}

      <section className="mb-12">

        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#D3AF37] flex items-center gap-3">
          💵 Total Claimable Amounts
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#171717] p-8 rounded-3xl border border-[#2A2A2A] shadow-2xl">

          <div className="text-center">

            <h3 className="text-2xl font-bold text-[#D3AF37] mb-4 flex items-center justify-center gap-3">
              ⚡ Energy (Wallet + Pending)
            </h3>

            <div className="text-4xl md:text-5xl font-black text-[#D3AF37]">

              ${claimable.energy}

              <span className="text-xs text-gray-400 ml-1">
                (
                {usdToKind(
                  claimable.energy
                )}{" "}
                KIND)
              </span>

            </div>

            <div className="text-sm text-gray-300 mt-2 flex flex-col items-center gap-1">

              <span>
                💰 Settled: $
                {
                  data.energyWallet
                }

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    data.energyWallet
                  )}{" "}
                  KIND)
                </span>
              </span>

              <span>
                ⏳ Pending: $
                {
                  pendingBreakdown.pendingEnergy
                }

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    pendingBreakdown.pendingEnergy
                  )}{" "}
                  KIND)
                </span>
              </span>

            </div>

          </div>

          <div className="text-center">

            <h3 className="text-2xl font-bold text-[#D3AF37] mb-4 flex items-center justify-center gap-3">
              👥 Referral (Ready to Claim)
            </h3>

            <div className="text-4xl md:text-5xl font-black text-[#D3AF37]">

              ${claimable.referral}

              <span className="text-xs text-gray-400 ml-1">
                (
                {usdToKind(
                  claimable.referral
                )}{" "}
                KIND)
              </span>

            </div>

            <div className="text-sm text-gray-300 mt-2">

              💰 Settled: $
              {
                data.referralWallet
              }

              <span className="text-xs text-gray-400 ml-1">
                (
                {usdToKind(
                  data.referralWallet
                )}{" "}
                KIND)
              </span>

            </div>

          </div>

        </div>

        <div className="mt-8 flex justify-center">

          <button
            onClick={
              handleWithdraw
            }
            disabled={
              claimLoading ||
              (parseFloat(
                claimable.energy
              ) === 0 &&
                parseFloat(
                  claimable.referral
                ) === 0)
            }
            className="px-10 py-5 text-xl text-white font-bold rounded-2xl bg-[#D3AF37] hover:bg-[#B59410] shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {claimLoading
              ? "Processing..."
              : "💸 Claim Reward"}
          </button>

        </div>

      </section>

      {/* ==================================================
          GLOBAL STATS
      ================================================== */}

      <section className="mb-12">

        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#D3AF37] flex items-center gap-3">
          🌍 Global Stats
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          <Card
            title="Total Users"
            value={globalStats.users.toLocaleString()}
            icon="👥"
          />

          <Card
            title="Cards Sold"
            value={globalStats.cardsSold.toLocaleString()}
            icon="🎴"
          />

          <Card
            title="Total Deposits"
            value={`$${globalStats.totalDeposits}`}
            subtitle={`(${usdToKind(
              globalStats.totalDeposits
            )} KIND)`}
            color="yellow"
            icon="💎"
          />

          <Card
            title="Rewards Paid"
            value={`$${globalStats.rewardUsdPaid}`}
            subtitle={`(${usdToKind(
              globalStats.rewardUsdPaid
            )} KIND)`}
            color="purple"
            icon="🎁"
          />

          <Card
            title="Remaining Pool"
            value={`$${globalStats.poolBalance}`}
            subtitle={`(${usdToKind(
              globalStats.poolBalance
            )} KIND)`}
            color="green"
            icon="🏦"
          />

          <Card
            title="🔥 Burn Stats"
            value={`$${burnStats.totalBurnedUsd}`}
            subtitle={
              <>
                🔥{" "}
                {
                  burnStats.totalBurnedKind
                }{" "}
                KIND Burned

                <br />

                ⏳ Pending: $
                {
                  burnStats.pendingBurnUsd
                }

                <span className="text-xs text-gray-400 ml-1">
                  (
                  {usdToKind(
                    burnStats.pendingBurnUsd
                  )}{" "}
                  KIND)
                </span>
              </>
            }
            color="red"
            icon="🔥"
          />

        </div>

      </section>

      {/* ==================================================
          REFERRAL NETWORK
      ================================================== */}

      <section className="mb-12">

        <div className="bg-[#171717] p-8 rounded-3xl border border-[#2A2A2A] shadow-2xl">

          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#D3AF37] flex items-center gap-3 justify-center">
            👥 Referral Network
          </h2>

          <div className="grid grid-cols-2 gap-6 mb-8">

            <div className="text-center p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

              <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
                Direct Refs
              </div>

              <div className="text-3xl font-black text-[#D3AF37]">
                {
                  data.qualifiedRefs
                }
              </div>

            </div>

            <div className="text-center p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

              <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
                Refs Remaining
              </div>

              <div className="text-3xl font-black text-[#D3AF37]">
                {
                  data.refsRemaining
                }
              </div>

            </div>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-6">

            <div className="text-center p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

              <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
                Total Referrals
              </div>

              <div className="text-3xl font-black text-[#D3AF37]">
                {
                  refStats.totalReferrals
                }
              </div>

            </div>

            <div className="text-center p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

              <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
                Team Volume
              </div>

              <div className="text-3xl font-black text-[#D3AF37]">
                $
                {Number(
                  refStats.teamVolume ||
                    0
                ).toFixed(2)}
              </div>

            </div>

            <div className="text-center p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

              <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
                Direct Volume
              </div>

              <div className="text-3xl font-black text-[#D3AF37]">
                $
                {Number(
                  refStats.directVolume ||
                    0
                ).toFixed(2)}
              </div>

            </div>

          </div>

          <div className="text-center mb-8 p-6 bg-[#111111] rounded-2xl border border-[#2A2A2A]">

            <div className="text-sm text-[#D3AF37] uppercase tracking-wide mb-2">
              Your Rank
            </div>

            <div className="text-3xl font-black text-[#D3AF37]">
              {
                getRankName()
              }
            </div>

          </div>

          <div className="bg-[#111111] p-6 rounded-2xl border border-[#2A2A2A]">

            <p className="text-gray-400 mb-4 text-sm text-center uppercase tracking-wide">
              Share Your Link
            </p>

            <div className="flex flex-col sm:flex-row gap-3">

              <input
                value={
                  referralLink
                }
                readOnly
                className="flex-1 bg-black/70 border border-[#2A2A2A] p-4 rounded-xl text-sm font-mono focus:outline-none focus:border-[#D3AF37] focus:ring-2 focus:ring-[#D3AF37]/50"
              />

              <button
                onClick={() =>
                  copy(
                    referralLink
                  )
                }
                className="bg-[#D3AF37] hover:bg-[#B59410] px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap flex items-center gap-2 text-sm text-white"
              >
                📋 Copy
              </button>

            </div>

          </div>

        </div>

      </section>

      {/* ==================================================
          BATCHES
      ================================================== */}

      {batches.length > 0 && (
        <section className="mb-12">

          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#D3AF37] flex items-center gap-3">
            📋 Your Transaction (
            {batches.length})
          </h2>

          <div className="overflow-x-auto rounded-2xl border border-[#2A2A2A] bg-[#171717] shadow-2xl">

            <table className="w-full">

              <thead className="bg-[#D3AF37]/10">

                <tr className="border-b border-[#2A2A2A]">

                  <th className="p-6 text-left font-bold text-[#D3AF37] text-lg">
                    #
                  </th>

                  <th className="p-6 text-left font-bold text-[#D3AF37] text-lg">
                    Cards
                  </th>

                  <th className="p-6 text-left font-bold text-[#D3AF37] text-lg">
                    Runtime
                  </th>

                  <th className="p-6 text-left font-bold text-[#D3AF37] text-lg">
                    Daily
                  </th>

                  <th className="p-6 text-left font-bold text-[#D3AF37] text-lg">
                    Status
                  </th>

                </tr>

              </thead>

              <tbody>

                {batches.map(
                  (batch) => (
                    <tr
                      key={
                        batch.id
                      }
                      className="border-b border-[#2A2A2A] hover:bg-[#D3AF37]/5 transition-colors"
                    >

                      <td className="p-6 font-mono text-xl font-bold text-white">
                        #{batch.id}
                      </td>

                      <td className="p-6 font-semibold text-[#D3AF37]">

                        {batch.totalCards.toLocaleString()}

                        <span className="text-xs text-gray-500">
                          (
                          {
                            batch.paidCards
                          }{" "}
                          paid
                          {batch.bonusCards >
                            0 &&
                            ` +${batch.bonusCards} bonus`}
                          )
                        </span>

                      </td>

                      <td className="p-6 font-mono text-[#D3AF37]">
                        {
                          batch.runtime
                        }
                      </td>

                      <td className="p-6 font-mono text-[#D3AF37]">
                        {
                          batch.daily
                        }
                      </td>

                      <td
                        className={`p-6 font-bold text-lg ${
                          batch.active
                            ? "text-[#D3AF37]"
                            : "text-gray-500"
                        }`}
                      >
                        {batch.active
                          ? "✅ Active"
                          : "❌ Expired"}
                      </td>

                    </tr>
                  )
                )}

              </tbody>

            </table>

          </div>

        </section>
      )}

    </div>
  );
};

// ==================================================
// CARD COMPONENT
// ==================================================

const Card = ({
  title,
  value,
  subtitle,
  color = "blue",
  icon,
}) => {
  const colors = {
    blue: {
      bg: "from-[#1a2233] to-[#111827]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    green: {
      bg: "from-[#18261f] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    purple: {
      bg: "from-[#22172d] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    yellow: {
      bg: "from-[#2a2412] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    cyan: {
      bg: "from-[#12252a] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    indigo: {
      bg: "from-[#171f35] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },

    red: {
      bg: "from-[#2a1616] to-[#111111]",
      border: "border-[#2A2A2A]",
      text: "text-[#D3AF37]",
    },
  };

  const style =
    colors[color] ||
    colors.blue;

  return (
    <div
      className={`group p-8 rounded-3xl bg-gradient-to-br ${style.bg} ${style.border} backdrop-blur-xl shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 cursor-pointer border-opacity-70`}
    >

      <div className="flex items-center gap-4 mb-6">

        {icon && (
          <div
            className={`text-3xl p-3 bg-white/10 rounded-2xl ${style.text}`}
          >
            {icon}
          </div>
        )}

        <div>

          <p className="text-gray-400 text-sm font-semibold uppercase tracking-wide group-hover:text-white/70 transition-colors">
            {title}
          </p>

        </div>

      </div>

      <div>

        <h2
          className={`text-3xl md:text-4xl font-black ${style.text}`}
        >
          {value}
        </h2>

        {subtitle && (
          <p className="text-sm text-gray-400 mt-2 font-mono bg-black/30 px-3 py-1 rounded-xl inline-block">
            {subtitle}
          </p>
        )}

      </div>

    </div>
  );
};

export default Dashboard;